import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    Send,
    Sparkles,
    Bot,
    UserRound,
    Loader2,
    History,
    Trash2,
    Plus,
    X,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds";

const PROVIDERS = [
    { key: "openai", label: "GPT", tag: "gpt-5.4" },
    { key: "gemini", label: "Gemini", tag: "gemini-3-flash" },
];

const STARTERS = [
    "I've been feeling tired in the afternoons. What could help?",
    "My digestion feels heavy after meals. Any Ayurvedic tips?",
    "How do I start a simple morning routine (dinacharya)?",
    "What foods should I avoid for my dosha?",
];

function MessageBubble({ role, text }) {
    const isUser = role === "user";
    return (
        <div
            data-testid={
                isUser ? TID.guideUserBubble : TID.guideAssistantBubble
            }
            className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}
        >
            <span
                className={`grid h-8 w-8 shrink-0 place-items-center ${
                    isUser
                        ? "bg-veda-surface2 text-veda-onSurface"
                        : "bg-veda-brand/10 text-veda-brand"
                }`}
            >
                {isUser ? <UserRound size={14} /> : <Bot size={14} />}
            </span>
            <div
                className={`max-w-[85%] whitespace-pre-wrap border p-3 text-sm leading-relaxed md:text-[15px] ${
                    isUser
                        ? "border-veda-border bg-veda-surface2 text-veda-onSurface"
                        : "border-veda-brand/25 bg-veda-brand/5 text-veda-onSurface"
                }`}
            >
                {text}
            </div>
        </div>
    );
}

export default function AiGuide() {
    const { user, requireAuth } = useAuth();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [provider, setProvider] = useState("openai");
    const [sessionId, setSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [sending, setSending] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const bottomRef = useRef(null);

    useEffect(() => {
        if (user) loadSessions();
    }, [user]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.length, sending]);

    async function loadSessions() {
        try {
            const { data } = await api.get("/ai/guide/sessions");
            setSessions(data);
        } catch {
            /* noop */
        }
    }

    async function openSession(sid) {
        try {
            const { data } = await api.get("/ai/guide/history", {
                params: { session_id: sid },
            });
            setSessionId(sid);
            setMessages(data.messages || []);
            setShowHistory(false);
        } catch {
            toast.error("Could not load conversation.");
        }
    }

    function newChat() {
        setSessionId(null);
        setMessages([]);
        setShowHistory(false);
    }

    async function deleteSession(sid, e) {
        e.stopPropagation();
        if (!window.confirm("Delete this conversation?")) return;
        try {
            await api.delete(`/ai/guide/session/${sid}`);
            if (sessionId === sid) newChat();
            loadSessions();
        } catch {
            toast.error("Could not delete.");
        }
    }

    async function doSend(text) {
        setSending(true);
        // Optimistic append
        setMessages((prev) => [
            ...prev,
            { role: "user", text, message_id: `tmp_${Date.now()}` },
        ]);
        try {
            const { data } = await api.post("/ai/guide", {
                message: text,
                provider,
                session_id: sessionId,
            });
            setSessionId(data.session_id);
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    text: data.reply,
                    message_id: `res_${Date.now()}`,
                },
            ]);
            loadSessions();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : "The Guide is resting a moment. Try again.",
            );
            // Roll back the optimistic user message
            setMessages((prev) => prev.slice(0, -1));
        } finally {
            setSending(false);
        }
    }

    function submit(e) {
        e?.preventDefault?.();
        const text = input.trim();
        if (!text || sending) return;
        if (!user) {
            requireAuth(() => doSend(text));
            setInput("");
            return;
        }
        setInput("");
        doSend(text);
    }

    return (
        <div data-testid={TID.aiGuidePage} className="pb-24">
            {/* Header band */}
            <div className="border-b border-veda-border">
                <div className="veda-container-narrow py-8 md:py-10">
                    <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                        >
                            <ArrowLeft size={14} /> Home
                        </Link>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={newChat}
                                data-testid={TID.guideNewChat}
                                className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                            >
                                <Plus size={11} /> New chat
                            </button>
                            <button
                                onClick={() => setShowHistory(true)}
                                data-testid={TID.guideHistoryBtn}
                                className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                            >
                                <History size={11} /> History
                            </button>
                        </div>
                    </div>
                    <div className="mx-auto mt-4 max-w-3xl">
                        <div className="flex items-center gap-2">
                            <span className="grid h-9 w-9 place-items-center bg-veda-brand/10 text-veda-brand">
                                <Sparkles size={16} />
                            </span>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                                Ayurveda Guide
                            </p>
                        </div>
                        <h1 className="font-display mt-2 text-3xl text-veda-onSurface md:text-4xl">
                            Ask anything about your body.
                        </h1>
                        <p className="mt-2 text-sm text-veda-muted md:text-base">
                            An unhurried Ayurvedic companion. Not a replacement
                            for a Vaidya — the Guide will suggest booking a
                            consultation when something needs personal care.
                        </p>
                        {/* Provider switcher */}
                        <div className="mt-5 flex flex-wrap items-center gap-2">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                Model
                            </span>
                            {PROVIDERS.map((p) => (
                                <button
                                    key={p.key}
                                    onClick={() => setProvider(p.key)}
                                    data-testid={TID.guideProvider(p.key)}
                                    disabled={sending}
                                    className={`rounded-pill border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                                        provider === p.key
                                            ? "border-veda-inverse bg-veda-inverse text-veda-onInverse"
                                            : "border-veda-border bg-veda-surface text-veda-onSurface"
                                    }`}
                                >
                                    {p.label}{" "}
                                    <span className="ml-1 opacity-60">
                                        {p.tag}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Conversation */}
            <div className="veda-container-narrow py-6 md:py-8">
                <div className="mx-auto max-w-3xl">
                    {messages.length === 0 ? (
                        <div className="border border-veda-border bg-veda-surface p-6 md:p-8">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Start with a question
                            </p>
                            <h2 className="font-display mt-2 text-xl text-veda-onSurface md:text-2xl">
                                What's on your mind today?
                            </h2>
                            <div className="mt-5 grid gap-2 sm:grid-cols-2">
                                {STARTERS.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => {
                                            setInput(s);
                                        }}
                                        data-testid={TID.guideStarter(
                                            s.slice(0, 20).replace(/\W+/g, "-").toLowerCase(),
                                        )}
                                        className="border border-veda-border bg-veda-surface2 p-3 text-left text-xs text-veda-onSurface hover:border-veda-brand md:text-sm"
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <ul className="flex flex-col gap-4">
                            {messages.map((m) => (
                                <li key={m.message_id || `${m.role}-${m.text}`}>
                                    <MessageBubble role={m.role} text={m.text} />
                                </li>
                            ))}
                            {sending && (
                                <li>
                                    <div className="flex gap-3">
                                        <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                                            <Bot size={14} />
                                        </span>
                                        <div className="inline-flex items-center gap-2 border border-veda-brand/25 bg-veda-brand/5 px-3 py-2 text-xs text-veda-muted">
                                            <Loader2 size={12} className="animate-spin" />
                                            Thinking…
                                        </div>
                                    </div>
                                </li>
                            )}
                            <div ref={bottomRef} />
                        </ul>
                    )}
                </div>
            </div>

            {/* Input dock */}
            <div className="fixed inset-x-0 bottom-0 z-20 border-t border-veda-border bg-veda-surface/95 backdrop-blur">
                <form
                    onSubmit={submit}
                    className="veda-container-narrow py-3 md:py-4"
                >
                    <div className="mx-auto flex max-w-3xl items-end gap-2">
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    submit();
                                }
                            }}
                            rows={1}
                            placeholder="Ask the Guide… (Enter to send, Shift+Enter for a new line)"
                            data-testid={TID.guideInput}
                            className="max-h-40 flex-1 resize-none border border-veda-border bg-veda-surface px-3 py-2.5 text-sm focus:border-veda-brand focus:outline-none"
                        />
                        <button
                            type="submit"
                            disabled={sending || !input.trim()}
                            data-testid={TID.guideSend}
                            className="inline-flex items-center gap-1.5 bg-veda-brand px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-white disabled:opacity-60"
                        >
                            {sending ? (
                                <Loader2 size={13} className="animate-spin" />
                            ) : (
                                <Send size={13} />
                            )}
                            Send
                        </button>
                    </div>
                </form>
            </div>

            {/* History drawer */}
            {showHistory && (
                <div
                    onClick={() => setShowHistory(false)}
                    className="fixed inset-0 z-40 flex items-end bg-veda-inverse/40 backdrop-blur-sm md:items-center md:justify-center"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md border border-veda-border bg-veda-surface p-5"
                    >
                        <div className="flex items-center justify-between">
                            <p className="font-display text-xl text-veda-onSurface">
                                Your conversations
                            </p>
                            <button
                                onClick={() => setShowHistory(false)}
                                className="p-1 text-veda-muted"
                                aria-label="Close"
                            >
                                <X size={16} />
                            </button>
                        </div>
                        {sessions.length === 0 ? (
                            <p className="mt-4 text-sm text-veda-muted">
                                No conversations yet.
                            </p>
                        ) : (
                            <ul className="mt-4 max-h-96 divide-y divide-veda-divider overflow-y-auto">
                                {sessions.map((s) => (
                                    <li
                                        key={s.session_id}
                                        onClick={() => openSession(s.session_id)}
                                        className="flex cursor-pointer items-center gap-3 py-3 hover:bg-veda-surface2"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="line-clamp-1 text-sm font-semibold text-veda-onSurface">
                                                {s.title || "Untitled"}
                                            </p>
                                            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-veda-muted">
                                                {s.provider}
                                            </p>
                                        </div>
                                        <button
                                            onClick={(e) =>
                                                deleteSession(s.session_id, e)
                                            }
                                            className="p-1 text-veda-error"
                                            aria-label="Delete"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
